// Access-control system detail page. Lives under /access-control/:id and
// is the single screen where a PM connects a PDK system to Intrlock:
// assign cloud nodes to properties, sync, link people / devices, resolve
// the matcher's pending decisions in plain English. No separate ACS
// pages — everything is here.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { apiList } from '../api/apiClient';
import { deviceTypeIcon, intrlockTypeLabel, ProviderPill } from '../components/access/DeviceVisuals';
import { findEntityById } from '../utils/entityIdentity';
import { PropertyAssignmentPicker, type PropertyAssignmentValue } from '../components/access/PropertyAssignmentPicker';
import './AccessControlSystemDetail.css';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
const tok = () =>
  localStorage.getItem('intrlock_token') ||
  localStorage.getItem('jwt') ||
  localStorage.getItem('token') ||
  '';

interface CloudNode { id: string; name?: string; acs_external_id: string; property_id?: string | null; property_ids?: string[]; status?: string; }
interface AcsDevice { id: string; name?: string; device_type?: string; online?: boolean; external_device_id?: string | null; external_cloud_node_id?: string | null; cloud_node_name?: string; model?: string | null; serial_number?: string | null; firmware_version?: string | null; battery_percent?: number | null; battery_state?: string | null; last_seen_at?: string | null; property_id?: string | null; property_ids?: string[]; property_name?: string | null; state?: { locked?: boolean; dpsOpen?: boolean; communicationLost?: boolean; relayOpen?: boolean; [k: string]: any } | null; raw?: { locked?: boolean; dpsOpen?: boolean; communicationLost?: boolean; relayOpen?: boolean; state?: Record<string, any> | null; [k: string]: any } | null; capabilities?: string[] | null; }
interface Holder {
  id: string;
  acs_external_id: string;
  first_name?: string; last_name?: string; email?: string; phone?: string;
  intrlock_person_id?: string | null;
  link_source?: string | null;
  link_confidence?: number | null;
  is_intrlock_managed?: boolean;
  property_ids?: string[];
  cloud_node_ids?: string[];
  cloud_node_names?: string[];
  groups?: { id: string; name: string }[];
  suggestion?: { intrlock_person_id: string; score: number; reasons: string[] };
  candidates?: { intrlock_person_id: string; score: number; reasons: string[] }[];
}
interface Tenant { id: string; property_id?: string; first_name?: string; last_name?: string; email?: string; phone?: string; unit_number?: string; }
interface PendingDecision {
  kind: string;
  question: string;
  recommended: string;
  holder_ids: string[];
  tenant_ids: string[];
  options: { label: string; description: string; action: string; payload: any }[];
}

const fmt = (a?: string, b?: string) => `${a ?? ''} ${b ?? ''}`.trim() || '—';

const CompactAssignmentEditor: React.FC<{
  properties: { id: string; name: string }[];
  selectedIds: string[];
  primaryId: string | null;
  onSave(value: PropertyAssignmentValue): Promise<void>;
}> = ({ properties, selectedIds, primaryId, onSave }) => {
  const selected = properties.filter((property) => selectedIds.includes(String(property.id)));
  return (
    <details className="group min-w-[220px]">
      <summary className="flex min-h-8 cursor-pointer list-none items-center justify-between gap-2 rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500">
        <span className="flex min-w-0 flex-wrap gap-1">
          {selected.length ? selected.map((property) => (
            <span key={property.id} className="max-w-[160px] truncate rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-800">{property.name}</span>
          )) : <span className="text-amber-700">Unassigned</span>}
        </span>
        <span className="shrink-0 font-medium text-emerald-700 group-open:hidden">Edit assignment</span>
        <span className="hidden shrink-0 font-medium text-gray-500 group-open:inline">Close</span>
      </summary>
      <div className="mt-2 w-80 max-w-[calc(100vw-2rem)] rounded border border-gray-200 bg-white p-3 shadow-sm">
        <PropertyAssignmentPicker compact mode="autosave" properties={properties}
          selectedIds={selectedIds} primaryId={primaryId} onSave={onSave} />
      </div>
    </details>
  );
};

export const AccessControlSystemDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [system, setSystem] = useState<any>(null);
  const [mapping, setMapping] = useState<any>(null);
  const [deviceMapping, setDeviceMapping] = useState<any>(null);
  const [properties, setProperties] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const t = (searchParams.get('tab') || '').toLowerCase();
    const allowed = ['overview','cloud_nodes','people','devices','groups','settings'];
    return (allowed.includes(t) ? t : 'overview') as any;
  })();
  const [tab, setTab] = useState<'overview' | 'cloud_nodes' | 'people' | 'devices' | 'schedules' | 'groups' | 'fields' | 'settings'>(initialTab);
  const [showAvailable, setShowAvailable] = useState(false);
  const [fieldMappings, setFieldMappings] = useState<any>(null);
  const [newMapping, setNewMapping] = useState<{ defId: string; name: string; target: string; priority: string }>({ defId: '', name: '', target: 'phone', priority: '100' });

  const [customers, setCustomers] = useState<any[]>([]);
  // Guard: if the URL param itself is missing or "undefined" (sometimes a
  // stale row in the AccessControl list has no id, so navigating from it
  // produces /access-control/undefined), bail out to the list instead of
  // firing API calls that would 404 / 500.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const idIsValid = !!id && id !== 'undefined' && id !== 'null' && UUID_RE.test(id);
  useEffect(() => {
    if (!idIsValid) navigate('/access-control', { replace: true });
  }, [idIsValid, navigate]);

  const [guestHolders, setGuestHolders] = useState<any[]>([]);
  const [guestHoldersLoaded, setGuestHoldersLoaded] = useState(false);

  const checkedFetch = useCallback(async (url: string, init?: RequestInit) => {
    setActionError('');
    const response = await fetch(url, init);
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      const message = detail || `Request failed (${response.status})`;
      setActionError(message);
      throw new Error(message);
    }
    return response;
  }, []);

  const fetchAll = useCallback(async () => {
    if (!id || !idIsValid) return;
    setLoadError('');
    const getJson = async (path: string) => {
      const response = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (!response.ok) throw new Error(`${path} failed (${response.status})`);
      return response.json();
    };
    const results = await Promise.allSettled([
      getJson(`/acs-systems/${id}`),
      getJson(`/acs-systems/${id}/people-mapping`),
      getJson(`/acs-systems/${id}/device-mapping`),
      getJson(`/acs-systems/${id}/field-mappings`),
      apiList('properties'),
      apiList('customers'),
      getJson(`/acs-systems/${id}/guest-holders`),
    ]);
    const labels = ['system', 'people mapping', 'device mapping', 'field mappings', 'properties', 'customers', 'guest holders'];
    const failures = results.flatMap((result, index) => result.status === 'rejected' ? [labels[index]] : []);
    const value = (index: number): any => results[index].status === 'fulfilled' ? (results[index] as PromiseFulfilledResult<any>).value : null;
    if (results[0].status === 'fulfilled' && value(0)?.id) setSystem(value(0));
    else failures.push('system record');
    setMapping(value(1));
    setDeviceMapping(value(2));
    setFieldMappings(value(3));
    setProperties(Array.isArray(value(4)) ? value(4) : []);
    setCustomers(Array.isArray(value(5)) ? value(5) : []);
    setGuestHolders(Array.isArray(value(6)?.items) ? value(6).items : []);
    setGuestHoldersLoaded(Array.isArray(value(6)?.items));
    if (failures.length) setLoadError(`Could not load ${Array.from(new Set(failures)).join(', ')}. Available details are shown; refresh to retry.`);
  }, [id, idIsValid]);

  const addFieldMapping = async () => {
    const body: any = {
      intrlock_field: newMapping.target,
      priority: parseInt(newMapping.priority, 10) || 100,
    };
    if (newMapping.defId) body.acs_custom_field_def_id = newMapping.defId;
    if (newMapping.name) body.custom_field_name = newMapping.name;
    await checkedFetch(`${API}/acs-systems/${id}/field-mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
      body: JSON.stringify(body),
    });
    setNewMapping({ defId: '', name: '', target: 'phone', priority: '100' });
    await fetchAll();
  };

  const deleteFieldMapping = async (mappingId: string) => {
    await checkedFetch(`${API}/acs-systems/${id}/field-mappings/${mappingId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` },
    });
    await fetchAll();
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sync = async () => {
    setBusy('sync');
    try {
      await checkedFetch(`${API}/acs-systems/${id}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: '{}',
      });
      await fetchAll();
    } finally { setBusy(null); }
  };

  const assignCloudNode = async (cnId: string, assignment: PropertyAssignmentValue) => {
    await checkedFetch(`${API}/acs-systems/cloud-nodes/${cnId}/assign-property`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
      body: JSON.stringify(assignment),
    });
    await fetchAll();
  };

  const linkHolder = async (holderId: string, personId: string, allowDup = false) => {
    await fetch(`${API}/acs-systems/${id}/holder-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
      body: JSON.stringify({ acs_holder_id: holderId, intrlock_person_id: personId, allow_duplicate: allowDup }),
    });
    await fetchAll();
  };

  const unlinkHolder = async (holderId: string) => {
    await fetch(`${API}/acs-systems/holders/${holderId}/link`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${tok()}` },
    });
    await fetchAll();
  };

  const assignAcsDeviceProperty = async (acsDeviceId: string, assignment: PropertyAssignmentValue) => {
    await checkedFetch(`${API}/acs-systems/devices/${acsDeviceId}/assign-property`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
      body: JSON.stringify(assignment),
    });
    await fetchAll();
  };

  const decide = async (decision: PendingDecision, optionIndex: number) => {
    const opt = decision.options[optionIndex];
    if (opt.action === 'link') {
      const p = opt.payload || {};
      if (p.unlink_other_id) {
        await fetch(`${API}/acs-systems/holders/${p.unlink_other_id}/link`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` } });
      }
      await fetch(`${API}/acs-systems/${id}/holder-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ acs_holder_id: p.acs_holder_id, intrlock_person_id: p.intrlock_person_id, allow_duplicate: !!p.allow_duplicate }),
      });
      await fetchAll();
    } else if (opt.action === 'skip') {
      // No-op server side; the decision just stops appearing once the user dismisses it.
      await fetchAll();
    } else if (opt.action === 'review_dups') {
      // Same-person tenant merge — punt to a future endpoint; for now, surface the IDs.
      alert(`Review duplicate tenants:\n${(opt.payload?.tenant_ids || []).join('\n')}`);
    }
  };

  if (!system) return <div className="p-8" role={loadError ? 'alert' : undefined}>{loadError || 'Loading…'}</div>;

  const counts = mapping?.counts || {};
  const dCounts = deviceMapping?.counts || {};
  // Guest + delivery PDK holders that are currently active (still in the
  // Intrlock group, PIN not expired/revoked). Counted alongside tenants
  // because they're real people with PDK access right now.
  const activeGuestHolderCount = guestHolders.filter((g) => String(g.status).toLowerCase() === 'active').length;
  const mappedPeopleCount = mapping && guestHoldersLoaded
    ? (Number(counts.mapped_holders) || 0) + activeGuestHolderCount
    : '—';
  const pending: PendingDecision[] = mapping?.pending_decisions || [];
  const tenantById = new Map((mapping?.tenants || []).map((t: Tenant) => [t.id, t]));

  // Provider-aware language. PDK calls them Cloud Nodes / Devices / People;
  // Brivo calls the same concepts Sites / Access Points / Users.
  const provider = (system.provider || 'pdk').toLowerCase();
  const isBrivo = provider === 'brivo';
  const canSync = provider === 'pdk' || provider === 'unifi_access';
  const providerNames: Record<string, string> = {
    pdk: 'ProDataKey (PDK)',
    brivo: 'Brivo',
    unifi_access: 'UniFi Access',
    cbw: 'ControlByWeb',
    bridge: 'Intrlock Bridge',
  };
  const L = {
    site:        'Site',
    sitePlural:  'Sites',
    device:      isBrivo ? 'Access Point' : 'Device',
    devicePlural:isBrivo ? 'Access Points': 'Devices',
    person:      isBrivo ? 'User'         : 'Person',
    personPlural:isBrivo ? 'Users'        : 'People',
    providerName: providerNames[provider] || provider.replace(/_/g, ' '),
  };

  const customerName =
    customers.find((c: any) => c.id === system.customer_id || c.uuid === system.customer_id)?.name
    || customers.find((c: any) => c.id === system.customer_id || c.uuid === system.customer_id)?.company_name
    || '—';

  const linkedProperties = (system.cloud_nodes || [])
    .filter((cn: CloudNode) => cn.property_id)
    .map((cn: CloudNode) => findEntityById(properties, cn.property_id))
    .filter(Boolean);
  const linkedPropertyNames = Array.from(new Set(linkedProperties.map((p: any) => p?.name).filter(Boolean)));

  return (
    <div className="shadow-detail-page property-detail-page acs-system-detail-page">
      {loadError && <div role="alert" className="mb-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{loadError}</div>}
      {actionError && <div role="alert" className="mb-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">Changes were not saved: {actionError}</div>}
      <button onClick={() => navigate('/access-control')} className="acs-back-link">← Back to Access Control</button>

      {/* Header */}
      <Card className="acs-identity-card">
        <div className="acs-identity-main">
          <div className="acs-identity-copy">
            <small>INTRLOCK APP / ACCESS CONTROL SYSTEM</small>
            <h1>{system.name}</h1>
            <div className="acs-system-meta">
              <span className="mr-3"><ProviderPill provider={provider} label={L.providerName} /></span>
              {linkedPropertyNames.length > 0 && (
                <span className="mr-3">{linkedPropertyNames.join(', ')}</span>
              )}
              {system.last_synced_at && <span>last synced: {new Date(system.last_synced_at).toLocaleString()}</span>}
            </div>
          </div>
          <div className="acs-header-actions">
            <Button
              variant="secondary"
              onClick={async () => {
                setBusy('refresh');
                try {
                  await fetchAll();
                } finally { setBusy(null); }
              }}
              disabled={busy === 'refresh'}
              title="Reload access control system details"
            >
              <svg className="w-4 h-4 mr-1.5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
            </Button>
            {canSync && <Button onClick={sync} disabled={busy === 'sync'}>{busy === 'sync' ? 'Syncing…' : 'Sync now'}</Button>}
            <Button variant="secondary" onClick={() => setTab('people')}>Review {L.personPlural.toLowerCase()}</Button>
          </div>
        </div>

      </Card>

      <section className="detail-stats property-detail-stats acs-system-stats" aria-label="Access control system totals">
        <Chip label={L.sitePlural} value={system.cloud_node_count} />
        <Chip label={`${L.personPlural} mapped`} value={mappedPeopleCount} />
        <Chip label="Active Devices" value={dCounts.assigned ?? 0} />
        <Chip label="Available Devices" value={dCounts.available ?? system.device_count ?? 0} />
      </section>

      {/* Tabs — provider-specific labels */}
      <nav className="property-tabs acs-system-tabs" aria-label="Access control system detail sections">
        {([
          ['overview', 'Overview'],
          ['cloud_nodes', `${L.sitePlural} (${(system.cloud_nodes || []).length})`],
          ['devices', `${L.devicePlural} (${dCounts.available ?? system.device_count ?? 0})`],
          ['people', `${L.personPlural} (${mappedPeopleCount})`],
          !isBrivo && ['groups', 'Groups'],
          ['settings', 'Settings'],
        ].filter(Boolean) as unknown as readonly [string, string][]).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k as any)}
            className={tab === k ? 'active' : ''}
          >{label}</button>
        ))}
      </nav>

      <div className="acs-tab-container">

      {/* Overview */}
      {tab === 'overview' && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Overview</h2>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Delete ACS system "${system.name}"? This removes Intrlock's record of the system, all cloud-node assignments, and the linked acs_devices/acs_holders. The upstream ${L.providerName} system itself is NOT touched.`)) return;
                try {
                  const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                  const tk = localStorage.getItem('intrlock_token') || '';
                  const res = await fetch(`${apiBase}/acs-systems/${system.id ?? id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tk}` } });
                  if (!res.ok) throw new Error(await res.text());
                  navigate('/access-control');
                } catch (e: any) {
                  alert('Delete failed: ' + (e?.message ?? e));
                }
              }}
              className="text-xs font-medium text-red-700 hover:text-red-800 px-2 py-1 rounded border border-red-200 hover:bg-red-50"
            >
              Delete System
            </button>
          </div>
          <dl className="text-sm grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">System name</dt><dd className="font-medium"><EditableText value={system.name || ''} onSave={async (v) => {
              const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
              const tk = localStorage.getItem('intrlock_token') || '';
              const r = await fetch(`${apiBase}/acs-systems/${system.id ?? id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ name: v }) });
              if (!r.ok) throw new Error(await r.text());
              setSystem((s: any) => ({ ...s, name: v }));
            }} /></dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">System provider</dt><dd className="font-medium">{L.providerName}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">System added date</dt><dd className="font-medium">{system.created_at ? new Date(system.created_at).toLocaleDateString() : '—'}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">Last sync</dt><dd className="font-medium">{system.last_synced_at ? new Date(system.last_synced_at).toLocaleString() : '—'}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">Intrlock properties</dt><dd className="font-medium">{linkedPropertyNames.length ? linkedPropertyNames.join(', ') : <span className="text-amber-700">unassigned</span>}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">{L.sitePlural} assigned</dt><dd className="font-medium">{(system.cloud_nodes || []).filter((cn: CloudNode) => cn.property_id).length} of {(system.cloud_nodes || []).length}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">{L.personPlural} mapped</dt><dd className="font-medium">{mappedPeopleCount}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">Active devices</dt><dd className="font-medium">{dCounts.assigned ?? 0} of {dCounts.available ?? system.device_count ?? 0}</dd></div>
            <div className="flex justify-between border-b border-gray-100 py-1"><dt className="text-gray-500">Pending decisions</dt><dd className={`font-medium ${pending.length ? 'text-amber-700' : ''}`}>{pending.length}</dd></div>
          </dl>
        </Card>
      )}

      {/* Cloud Nodes / Sites */}
      {tab === 'cloud_nodes' && (
        <>
          <Card>
            <h2 className="font-semibold mb-3">{L.sitePlural} → property assignment</h2>
            <p className="text-sm text-gray-500 mb-4">Each {L.site.toLowerCase()} represents one building. A property can host multiple {L.sitePlural.toLowerCase()} if needed.</p>
            <div className="overflow-visible border-y border-gray-200">
              <div className="hidden grid-cols-[minmax(220px,1fr)_140px_minmax(280px,auto)] gap-4 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 md:grid">
                <span>{L.site}</span><span>Status</span><span>Intrlock properties</span>
              </div>
              {(system.cloud_nodes || []).map((cn: CloudNode) => (
                <div key={cn.id} className="grid gap-2 border-t border-gray-100 px-3 py-2.5 first:border-t-0 md:grid-cols-[minmax(220px,1fr)_140px_minmax(280px,auto)] md:items-center md:gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">
                      <EditableText
                        value={cn.name || ''}
                        placeholder={`Unnamed ${L.site.toLowerCase()}`}
                        onSave={async (v) => {
                          const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                          const tk = localStorage.getItem('intrlock_token') || '';
                          const r = await fetch(`${apiBase}/acs-systems/cloud-nodes/${cn.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ name: v }) });
                          if (!r.ok) throw new Error(await r.text());
                          setSystem((s: any) => ({ ...s, cloud_nodes: (s.cloud_nodes || []).map((x: any) => x.id === cn.id ? { ...x, name: v } : x) }));
                        }}
                      />
                    </p>
                  </div>
                  <div className="text-xs text-gray-600 md:block"><span className="mr-1 text-gray-400 md:hidden">Status:</span>{cn.status || '—'}</div>
                  <CompactAssignmentEditor properties={properties}
                    selectedIds={cn.property_ids || (cn.property_id ? [cn.property_id] : [])}
                    primaryId={cn.property_id || null} onSave={(value) => assignCloudNode(cn.id, value)} />
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Schedules — PDK only */}
      {tab === 'schedules' && !isBrivo && (
        <Card>
          <h2 className="font-semibold mb-3">Schedules</h2>
          <p className="text-sm text-gray-500">Synced from {L.providerName}. Schedules govern when groups can unlock devices.</p>
          {(system.schedules || []).length === 0
            ? <p className="text-sm text-gray-400 italic mt-3 px-2 py-3 bg-gray-50 rounded">— none synced yet —</p>
            : <ul className="text-sm divide-y divide-gray-100 mt-3">
                {(system.schedules || []).map((s: any) => (
                  <li key={s.id} className="py-2 flex items-center justify-between">
                    <span className="font-medium">{s.name || 'Unnamed schedule'}</span>
                    <span className="text-xs text-gray-500">{s.kind || s.schedule_type || ''}</span>
                  </li>
                ))}
              </ul>}
        </Card>
      )}

      {/* Groups — PDK only. Each row expands to show members, doors, and
          schedules so the PM can verify what each group actually grants. */}
      {tab === 'groups' && !isBrivo && (
        <GroupsTab groups={system.groups || []} L={L} />
      )}

      {/* Settings */}
      {tab === 'settings' && (
        <Card>
          <h2 className="font-semibold mb-3">Settings</h2>
          <p className="text-sm text-gray-500 mb-4">Control how Intrlock reads from and writes to {L.providerName}.</p>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-3">
              <PropertyAssignmentPicker properties={properties}
                selectedIds={system.property_ids || (system.property_id ? [system.property_id] : [])}
                primaryId={system.property_id || null}
                onSave={async (value) => {
                  await checkedFetch(`${API}/acs-systems/${id}/assign-property`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                    body: JSON.stringify(value),
                  });
                  await fetchAll();
                }} />
            </div>
            <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3">
              <div>
                <p className="font-medium text-sm">Read access</p>
                <p className="text-xs text-gray-500 mt-1">Intrlock pulls {L.sitePlural.toLowerCase()}, {L.devicePlural.toLowerCase()}, {L.personPlural.toLowerCase()}, and schedules.</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Enabled</span>
            </div>

            <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3">
              <div>
                <p className="font-medium text-sm">Write access</p>
                <p className="text-xs text-gray-500 mt-1">Intrlock pushes {L.personPlural.toLowerCase()} + credentials back into {L.providerName} when tenants are added or moved.</p>
              </div>
              <select
                value={system.write_mode || 'managed'}
                onChange={async (e) => {
                  await checkedFetch(`${API}/acs-systems/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                    body: JSON.stringify({ write_mode: e.target.value }),
                  });
                  await fetchAll();
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="off">Off (read-only)</option>
                <option value="managed">Managed (Intrlock writes)</option>
              </select>
            </div>

            <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3">
              <div>
                <p className="font-medium text-sm">Group management</p>
                <p className="text-xs text-gray-500 mt-1">Intrlock always uses groups it owns and never modifies provider-owned groups.</p>
              </div>
              <select
                value={system.managed_group_strategy || 'shared'}
                onChange={async (e) => {
                  const managed_group_strategy = e.target.value;
                  const previewResponse = await checkedFetch(`${API}/acs-systems/${id}/group-strategy`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                    body: JSON.stringify({ managed_group_strategy, apply: false }),
                  });
                  const preview = await previewResponse.json();
                  if (!window.confirm(`Apply this group strategy? ${preview.managed_holders || 0} managed holders will be reconciled after destination groups are ready.`)) return;
                  await checkedFetch(`${API}/acs-systems/${id}/group-strategy`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                    body: JSON.stringify({ managed_group_strategy, apply: true }),
                  });
                  await fetchAll();
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="shared">One group for all managed users</option>
                <option value="segmented">Separate tenant, guest, and vendor groups</option>
              </select>
            </div>
          </div>
        </Card>
      )}

      {/* People — Intrlock now AUTO-PROVISIONS one PDK holder per active
          tenant per property (the "Intrlock — <Property>" managed group).
          The page shifts focus from "import + match" → "verify provisioning":
            1. Tenants by Property — every active tenant + provisioning state.
            2. External card holders — pre-existing PDK holders Intrlock did
               NOT create, kept read-only as context.
            3. Drift / issues — tenants missing a holder or with link
               conflicts, with a per-property Re-provision button. */}
      {tab === 'people' && (() => {
        const allH = (mapping?.holders || []) as Holder[];
        const tenants = (mapping?.tenants || []) as Tenant[];

        // Property naming — synced cloud_nodes first, properties[] fallback.
        const propNameById = new Map<string, string>();
        for (const cn of (system.cloud_nodes || []) as CloudNode[]) {
          const cnP = (cn as any).property_id;
          const cnPName = (cn as any).property_name;
          if (cnP && cnPName) propNameById.set(cnP, cnPName);
        }
        for (const p of properties) {
          if (p?.id && p?.name) propNameById.set(p.id, p.name);
        }

        const holderByPersonId = new Map<string, Holder>();
        for (const h of allH) {
          if (h.intrlock_person_id) holderByPersonId.set(h.intrlock_person_id, h);
        }

        // Bucket tenants by property; properties with no tenants still show
        // up if a cloud-node is linked there (so the PM sees an empty
        // section rather than wondering "did sync miss this property?").
        const propIds = Array.from(new Set([
          ...tenants.map((t) => t.property_id || '').filter(Boolean) as string[],
          ...((system.cloud_nodes || []) as CloudNode[]).map((cn) => (cn as any).property_id).filter(Boolean),
        ]));
        const tenantsByProperty = new Map<string, Tenant[]>();
        for (const pid of propIds) tenantsByProperty.set(pid, []);
        for (const t of tenants) {
          const pid = t.property_id || '__no_property__';
          (tenantsByProperty.get(pid) ?? tenantsByProperty.set(pid, []).get(pid)!).push(t);
        }
        const propertyKeys = Array.from(tenantsByProperty.keys()).sort((a, b) =>
          (propNameById.get(a) || a).localeCompare(propNameById.get(b) || b),
        );

        const reprovisionProperty = async (propertyId: string) => {
          if (!window.confirm('Update ACS tenants for this property? Creates any missing PDK holders and adds them to the Intrlock-managed group. Existing holders are left alone.')) return;
          setBusy(`provision:${propertyId}`);
          try {
            const r = await fetch(`${API}/acs-systems/${id}/provision-property/${propertyId}`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${tok()}` },
            });
            if (!r.ok) throw new Error(await r.text());
            await fetchAll();
          } catch (err: any) {
            alert(`Update ACS tenants failed: ${err?.message ?? err}`);
          } finally {
            setBusy(null);
          }
        };

        // Counters for the summary row at the top.
        const totalTenants = tenants.length;
        const provisionedTenants = tenants.filter((t) => {
          const h = holderByPersonId.get(t.id);
          return h?.is_intrlock_managed;
        }).length;
        const missing = totalTenants - provisionedTenants;

        const TenantRow = (t: Tenant) => {
          const h = holderByPersonId.get(t.id);
          // Simple binary: is the tenant in the Intrlock-managed group on
          // this ACS system, or not? Pre-existing external PDK holders
          // (vendors / card-only users) are intentionally ignored here.
          const inIntrlock = !!h?.is_intrlock_managed;
          return (
            <tr key={t.id}>
              <td className="py-2">
                <p className="font-medium">{fmt(t.first_name, t.last_name)}</p>
                {t.unit_number && <p className="text-xs text-gray-500">Unit {t.unit_number}</p>}
              </td>
              <td className="py-2 text-xs text-gray-600">
                {t.email || '—'}<br/>{t.phone || '—'}
              </td>
              <td className="py-2">
                {inIntrlock
                  ? <Badge color="green">In Intrlock group</Badge>
                  : <Badge color="amber">Not in Intrlock group</Badge>}
              </td>
              <td className="py-2 text-right" />
            </tr>
          );
        };

        return (
          <div className="space-y-4">
            {/* Summary banner — at-a-glance provisioning health. */}
            <Card>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="text-xs uppercase tracking-wider text-gray-500">In Intrlock group</span>
                  <p className="text-2xl font-bold text-green-700">{provisionedTenants}<span className="text-sm font-normal text-gray-400"> / {totalTenants}</span></p>
                </div>
                {missing > 0 && (
                  <div>
                    <span className="text-xs uppercase tracking-wider text-gray-500">Not in group</span>
                    <p className="text-2xl font-bold text-amber-700">{missing}</p>
                  </div>
                )}
                <div className="ml-auto text-xs text-gray-500 max-w-md">
                  Intrlock auto-creates one {L.providerName} {L.person.toLowerCase()} per active tenant in the
                  <code className="bg-gray-100 px-1 rounded mx-1">Intrlock — &lt;Property&gt;</code>
                  group. Use <strong>Update ACS tenants</strong> on a property to fix any drift.
                </div>
              </div>
            </Card>

            {/* Per-property tenant rosters. */}
            {propertyKeys.length === 0 ? (
              <Card>
                <p className="text-sm text-gray-500">No properties linked to this {L.providerName} system yet — assign sites to Intrlock properties on the {L.sitePlural} tab.</p>
              </Card>
            ) : propertyKeys.map((propKey) => {
              const propLabel = propKey === '__no_property__'
                ? '— No Intrlock property —'
                : (propNameById.get(propKey) || 'Unknown property');
              const rows = tenantsByProperty.get(propKey) || [];
              const propProvisioned = rows.filter((t) => holderByPersonId.get(t.id)?.is_intrlock_managed).length;
              const propMissing = rows.length - rows.filter((t) => !!holderByPersonId.get(t.id)).length;
              return (
                <Card key={propKey}>
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{propLabel}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {rows.length} active tenant{rows.length === 1 ? '' : 's'} ·
                        {' '}{propProvisioned} auto-provisioned
                        {propMissing > 0 && <> · <span className="text-amber-700 font-medium">{propMissing} need provisioning</span></>}
                      </p>
                    </div>
                    {propKey !== '__no_property__' && (
                      <button
                        onClick={() => reprovisionProperty(propKey)}
                        disabled={busy === `provision:${propKey}`}
                        className="text-xs px-3 py-1.5 rounded border border-brand-600 text-brand-700 hover:bg-brand-50 disabled:opacity-50"
                      >
                        {busy === `provision:${propKey}` ? 'Updating ACS tenants…' : 'Update ACS tenants'}
                      </button>
                    )}
                  </div>
                  {rows.length === 0
                    ? <p className="text-xs text-gray-400 italic px-2 py-3 bg-gray-50 rounded">No active tenants on this property yet.</p>
                    : <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                            <tr><th className="py-2">Tenant</th><th className="py-2">Email / Phone</th><th className="py-2">Status</th><th className="py-2"></th></tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {rows.map(TenantRow)}
                          </tbody>
                        </table>
                      </div>}
                </Card>
              );
            })}

            {/* ── Guests in {provider} (active right now) ─────────────── */}
            {(() => {
              const fmtTime = (iso?: string | null) => {
                if (!iso) return '—';
                try { return new Date(iso).toLocaleString(); } catch { return iso; }
              };
              const isActive = (g: any) => String(g.status).toLowerCase() === 'active';
              const guestRows = guestHolders.filter((g) => g.kind === 'guest' && isActive(g));
              const deliveryRows = guestHolders.filter((g) => g.kind === 'delivery' && isActive(g));
              const inactiveCount = guestHolders.length - guestRows.length - deliveryRows.length;
              const HolderTable: React.FC<{ rows: any[]; kindLabel: string }> = ({ rows: rs, kindLabel }) => (
                rs.length === 0
                  ? <p className="text-xs text-gray-400 italic px-2 py-3 bg-gray-50 rounded">No active {kindLabel} on this system right now.</p>
                  : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          <tr>
                            <th className="py-2">Name</th>
                            <th className="py-2">Property</th>
                            <th className="py-2">PIN</th>
                            <th className="py-2">Expires</th>
                            <th className="py-2">Used</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {rs.map((g) => (
                            <tr key={g.acs_holder_id}>
                              <td className="py-2">
                                <p className="font-medium">{fmt(g.first_name, g.last_name) || g.visitor_name || '—'}</p>
                                {g.delivery_carrier && <p className="text-xs text-gray-500">{g.delivery_carrier}</p>}
                              </td>
                              <td className="py-2 text-xs text-gray-600">{g.property_name || '—'}</td>
                              <td className="py-2 text-xs font-mono text-gray-700">{g.pin_code || '—'}</td>
                              <td className="py-2 text-xs text-gray-600">{fmtTime(g.expires_at)}</td>
                              <td className="py-2 text-xs text-gray-600">
                                {g.last_used_at
                                  ? <>{fmtTime(g.last_used_at)}<br/><span className="text-[10px] text-gray-400">{g.use_count}× used</span></>
                                  : <span className="italic text-gray-400">Unused</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
              );
              return (
                <>
                  <Card>
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">Active Guests</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {guestRows.length} active guest PIN{guestRows.length === 1 ? '' : 's'} currently in the Intrlock-managed group on this {L.providerName} system.
                        </p>
                      </div>
                    </div>
                    <HolderTable rows={guestRows} kindLabel="guests" />
                  </Card>

                  <Card>
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">Active Deliveries</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {deliveryRows.length} active delivery code{deliveryRows.length === 1 ? '' : 's'} currently in the Intrlock-managed group on this {L.providerName} system.
                        </p>
                      </div>
                    </div>
                    <HolderTable rows={deliveryRows} kindLabel="deliveries" />
                    {inactiveCount > 0 && (
                      <p className="text-[11px] text-gray-400 mt-2">
                        {inactiveCount} expired or revoked PIN{inactiveCount === 1 ? '' : 's'} hidden — their PDK holders are cleaned up automatically.
                      </p>
                    )}
                  </Card>
                </>
              );
            })()}

          </div>
        );
      })()}

      {/* Devices — assign each ACS device to an Intrlock property. */}
      {tab === 'devices' && (() => {
        const all = (deviceMapping?.acs_devices || []) as AcsDevice[];
        const properties = (deviceMapping?.properties || []) as { id: string; name: string }[];
        const assigned   = all.filter((d) => d.property_ids?.length || d.property_id);
        const unassigned = all.filter((d) => !(d.property_ids?.length || d.property_id));
        const StatePills = ({ ad }: { ad: AcsDevice }) => {
          const st: any = ad.state || ad.raw?.state || ad.raw || {};
          const pills: React.ReactNode[] = [];
          if (st.communicationLost === true) {
            pills.push(<span key="off" className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800">Offline</span>);
          }
          if (typeof st.locked === 'boolean') {
            pills.push(<span key="lk" className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${st.locked ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{st.locked ? 'Locked' : 'Unlocked'}</span>);
          }
          if (st.dpsOpen === true) {
            pills.push(<span key="dps" className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800">Door Open</span>);
          }
          if (st.relayOpen === true) {
            pills.push(<span key="rly" className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800">Relay Open</span>);
          }
          if (pills.length === 0) return null;
          return <div className="flex flex-wrap gap-1 mt-1">{pills}</div>;
        };
        const Row = (ad: AcsDevice) => {
          const stAny: any = (ad.state || ad.raw?.state || ad.raw || {}) as any;
          const offline = stAny.communicationLost === true || ad.online === false;
          return (
          <tr key={ad.id}>
            <td className="py-2">
              <div className="flex items-start gap-2">
                {deviceTypeIcon({ device_type: ad.device_type, name: ad.name }, offline)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium">
                    <EditableText
                      value={ad.name || ''}
                      placeholder={`Unnamed ${L.device.toLowerCase()}`}
                      onSave={async (v) => {
                        const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                        const tk = localStorage.getItem('intrlock_token') || '';
                        const r = await fetch(`${apiBase}/acs-systems/devices/${ad.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ name: v }) });
                        if (!r.ok) throw new Error(await r.text());
                        setSystem((s: any) => ({ ...s, devices: (s.devices || []).map((x: any) => x.id === ad.id ? { ...x, name: v } : x) }));
                      }}
                    />
                  </p>
                  <p className="text-xs text-gray-400">{intrlockTypeLabel(ad)}</p>
                  <StatePills ad={ad} />
                  <details className="mt-1 text-[11px] text-gray-500">
                    <summary className="cursor-pointer font-medium text-gray-500 hover:text-emerald-700">Technical details</summary>
                    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 rounded bg-gray-50 p-2">
                      <dt>Provider ID</dt><dd className="break-all font-mono">{ad.external_device_id || '—'}</dd>
                      {ad.model && <><dt>Model</dt><dd>{ad.model}</dd></>}
                      {ad.serial_number && <><dt>Serial</dt><dd className="break-all font-mono">{ad.serial_number}</dd></>}
                      {ad.firmware_version && <><dt>Firmware</dt><dd>{ad.firmware_version}</dd></>}
                      {(ad.battery_percent != null || ad.battery_state) && <><dt>Battery</dt><dd>{ad.battery_percent != null ? `${ad.battery_percent}%` : ad.battery_state}</dd></>}
                      {ad.external_cloud_node_id && <><dt>{L.site} ID</dt><dd className="break-all font-mono">{ad.external_cloud_node_id}</dd></>}
                      {!!ad.capabilities?.length && <><dt>Capabilities</dt><dd>{ad.capabilities.join(', ')}</dd></>}
                    </dl>
                  </details>
                </div>
              </div>
            </td>
            <td className="py-2 text-xs text-gray-600">
              <span>{ad.cloud_node_name || '—'}</span>
            </td>
            <td className="py-2">
              <CompactAssignmentEditor properties={properties}
                selectedIds={ad.property_ids || (ad.property_id ? [ad.property_id] : [])}
                primaryId={ad.property_id || null} onSave={(value) => assignAcsDeviceProperty(ad.id, value)} />
            </td>
          </tr>
          );
        };
        const Section = ({ title, rows, color, emptyMsg }: { title: string; rows: AcsDevice[]; color: string; emptyMsg?: string }) => (
          <div className="mb-6">
            <h3 className={`font-semibold mb-2 ${color}`}>{title} ({rows.length})</h3>
            {rows.length === 0
              ? <p className="text-sm text-gray-400 italic px-2 py-3 bg-gray-50 rounded">{emptyMsg || '— none —'}</p>
              : <div className="shadow-table-scroll overflow-x-auto"><table className="min-w-[680px] w-full text-sm"><thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200"><tr><th className="py-2">{L.providerName} {L.device.toLowerCase()}</th><th className="py-2">{L.site}</th><th className="py-2">Intrlock property</th></tr></thead><tbody className="divide-y divide-gray-100">{rows.map(Row)}</tbody></table></div>}
          </div>
        );
        return (
          <Card>
            <p className="text-sm text-gray-500 mb-4">
              Every {L.device.toLowerCase()} anchored to this {L.providerName} system is listed below. A device may be assigned once to each of multiple properties; its primary property is the default for operations that do not specify one. Provisional {L.devicePlural.toLowerCase()} were imported from {L.providerName} but are not assigned yet.
            </p>
            <Section
              title={`Provisional / Unassigned`}
              rows={unassigned}
              color="text-amber-800"
              emptyMsg="— no provisional devices; all imported devices are already assigned —"
            />
            <Section title="Assigned" rows={assigned} color="text-green-800" />
          </Card>
        );
      })()}

      {/* Field mappings — PDK custom field → Intrlock canonical column */}
      {tab === 'fields' && (
        <Card>
          <h2 className="font-semibold mb-3">Custom field mappings</h2>
          <p className="text-sm text-gray-500 mb-4">
            Tell Intrlock which PDK custom field carries which canonical value. Useful when a PM created a custom PDK field like
            <code className="mx-1 px-1 bg-gray-100 rounded">intrlock_phone</code> that should drive the tenant's phone number.
          </p>

          {/* Existing mappings */}
          <div className="mb-5">
            <h3 className="font-medium text-sm mb-2">Active mappings ({fieldMappings?.mappings?.length || 0})</h3>
            {(fieldMappings?.mappings || []).length === 0
              ? <p className="text-sm text-gray-400 italic px-2 py-3 bg-gray-50 rounded">No mappings yet — add one below.</p>
              : <div className="space-y-2">{(fieldMappings.mappings || []).map((m: any) => (
                  <div key={m.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg text-sm">
                    <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 rounded">{m.custom_field_def_name || m.custom_field_name || '—'}</span>
                    <span className="text-gray-500">→</span>
                    <span className="font-medium">{m.intrlock_field}</span>
                    <span className="text-xs text-gray-500">priority {m.priority ?? 100}</span>
                    <div className="ml-auto"><button onClick={() => deleteFieldMapping(m.id)} className="text-xs text-red-600 hover:underline">Remove</button></div>
                  </div>
                ))}</div>}
          </div>

          {/* Add new */}
          <div className="border-t pt-4">
            <h3 className="font-medium text-sm mb-2">Add a mapping</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
              <div>
                <label className="text-xs text-gray-500">PDK custom field</label>
                <select value={newMapping.defId} onChange={(e) => setNewMapping({ ...newMapping, defId: e.target.value, name: '' })} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
                  <option value="">— pick from synced —</option>
                  {(fieldMappings?.pdk_custom_field_defs || []).map((d: any) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">or type a name below if not yet synced</p>
                <input type="text" placeholder="e.g. intrlock_phone" value={newMapping.name} onChange={(e) => setNewMapping({ ...newMapping, name: e.target.value, defId: '' })} className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Intrlock target</label>
                <select value={newMapping.target} onChange={(e) => setNewMapping({ ...newMapping, target: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
                  <option value="phone">Phone number</option>
                  <option value="email">Email</option>
                  <option value="unit_number">Unit number</option>
                  <option value="first_name">First name</option>
                  <option value="last_name">Last name</option>
                  <option value="external_ref">External reference</option>
                  {(fieldMappings?.intrlock_custom_field_defs || []).map((d: any) => (
                    <option key={d.id} value={`custom:${d.id}`}>Intrlock custom: {d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Priority (lower wins)</label>
                <input type="number" value={newMapping.priority} onChange={(e) => setNewMapping({ ...newMapping, priority: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
              </div>
              <div className="flex items-end">
                <Button onClick={addFieldMapping} disabled={!newMapping.defId && !newMapping.name}>Add mapping</Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      </div>

    </div>
  );
};

interface GroupRow {
  id: string;
  name?: string;
  schedule_name?: string;
  holder_count?: number | string;
  device_count?: number | string;
  members?: { id: string; first_name?: string; last_name?: string; email?: string; phone?: string; intrlock_person_id?: string | null }[];
  devices?: { id: string; name?: string; device_type?: string; cloud_node_name?: string; property_name?: string; schedule_name?: string | null }[];
  schedules?: { id: string; name?: string; weekly_json?: any; holiday_json?: any }[];
}

// Lightweight inline-edit text. Click to enter edit mode, Enter to save,
// Esc to cancel. Used for system / cloud node / device renames.
const EditableText: React.FC<{ value: string; onSave: (v: string) => Promise<void> | void; placeholder?: string; className?: string }> = ({ value, onSave, placeholder, className }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  React.useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  const commit = async () => {
    if (busy) return;
    const trimmed = draft.trim();
    if (trimmed === value) { setEditing(false); return; }
    setBusy(true); setErr(null);
    try { await onSave(trimmed); setEditing(false); }
    catch (e: any) { setErr(String(e?.message ?? e).slice(0, 120)); }
    finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} className={`text-left hover:underline decoration-dotted ${className ?? ''}`} title="Click to edit">
        {value || <span className="text-gray-400">{placeholder || '—'}</span>}
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1">
      <input
        autoFocus
        value={draft}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); }}}
        className="border border-gray-300 rounded px-2 py-0.5 text-sm w-48"
      />
      <button onClick={commit} disabled={busy} className="text-xs text-emerald-700 px-1">{busy ? '…' : '✓'}</button>
      <button onClick={() => { setDraft(value); setEditing(false); }} className="text-xs text-gray-500 px-1">✕</button>
      {err && <span className="text-xs text-red-600 ml-2">{err}</span>}
    </div>
  );
};

const ProvisionPanel: React.FC<{
  systemId: string;
  providerName: string;
  properties: any[];
  cloudNodes: CloudNode[];
}> = ({ systemId, providerName, properties, cloudNodes }) => {
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, any>>({});

  // Show only properties that actually have a cloud node assigned to this
  // system. Provisioning a property with no doors makes no sense.
  const assignedPropertyIds = new Set(
    cloudNodes.filter((c) => c.property_id).map((c) => String(c.property_id)),
  );
  const targets = properties.filter((p) => assignedPropertyIds.has(String(p.id)));

  if (targets.length === 0) return null;

  const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
  const tk = () => localStorage.getItem('intrlock_token') || '';

  const provisionOne = async (propertyId: string) => {
    if (!window.confirm(`Create the Intrlock-managed group + holders on ${providerName} for this property? This writes new records to ${providerName}.`)) return;
    setBusy((b) => ({ ...b, [propertyId]: true }));
    try {
      const res = await fetch(`${apiBase}/acs-systems/${systemId}/provision-property/${propertyId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tk()}` },
      });
      const data = await res.json().catch(() => ({}));
      setResults((r) => ({ ...r, [propertyId]: { ok: res.ok, data } }));
    } catch (e: any) {
      setResults((r) => ({ ...r, [propertyId]: { ok: false, data: { error: String(e?.message ?? e) } } }));
    } finally {
      setBusy((b) => ({ ...b, [propertyId]: false }));
    }
  };

  return (
    <Card>
      <h2 className="font-semibold mb-2">Intrlock-managed access</h2>
      <p className="text-sm text-gray-500 mb-4">
        Create a dedicated <span className="font-medium">Intrlock</span> group on {providerName} for each property
        and mirror its tenants as new {providerName} holders. We never modify
        records that aren't ours. Per-property — pick which to provision.
      </p>
      <div className="space-y-2">
        {targets.map((p: any) => {
          const r = results[p.id];
          const isBusy = !!busy[p.id];
          return (
            <div key={p.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{p.name}</p>
                {r?.ok && r.data && (
                  <p className="text-xs text-emerald-700">
                    {r.data.group_created ? 'Group created' : 'Group reused'} ·
                    {' '}{r.data.rules_devices} {r.data.rules_devices === 1 ? 'door rule' : 'door rules'} ·
                    {' '}{r.data.holders_created} new holder{r.data.holders_created === 1 ? '' : 's'},
                    {' '}{r.data.holders_existing} reused ·
                    {' '}{r.data.group_members} member{r.data.group_members === 1 ? '' : 's'}
                    {Array.isArray(r.data.errors) && r.data.errors.length > 0 && (
                      <span className="text-amber-700"> · {r.data.errors.length} warning{r.data.errors.length === 1 ? '' : 's'}</span>
                    )}
                  </p>
                )}
                {r && !r.ok && (
                  <p className="text-xs text-red-700">Failed: {r.data?.error ?? 'unknown error'}</p>
                )}
              </div>
              <Button size="sm" disabled={isBusy} onClick={() => provisionOne(p.id)}>
                {isBusy ? 'Provisioning…' : (r?.ok ? 'Re-provision' : 'Provision')}
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const GroupsTab: React.FC<{ groups: GroupRow[]; L: any }> = ({ groups, L }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) => {
    const s = new Set(open);
    s.has(id) ? s.delete(id) : s.add(id);
    setOpen(s);
  };
  return (
    <Card>
      <h2 className="font-semibold mb-1">Groups</h2>
      <p className="text-sm text-gray-500 mb-3">{L.providerName} groups bundle holders + devices + a schedule into a single access rule. Click a group to expand.</p>
      {groups.length === 0
        ? <p className="text-sm text-gray-400 italic mt-3 px-2 py-3 bg-gray-50 rounded">— none synced yet —</p>
        : <div className="divide-y divide-gray-100">
            {groups.map((g) => {
              const isOpen = open.has(g.id);
              const members = g.members || [];
              const devices = g.devices || [];
              const schedules = g.schedules || [];
              return (
                <div key={g.id} className="py-2">
                  <button
                    onClick={() => toggle(g.id)}
                    className="w-full flex items-center justify-between text-left hover:bg-gray-50 rounded px-2 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 w-3">{isOpen ? '▾' : '▸'}</span>
                      <span className="font-medium">{g.name || 'Unnamed group'}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {Number(g.holder_count || 0)} {Number(g.holder_count || 0) === 1 ? 'person' : 'people'}
                      {' · '}{Number(g.device_count || 0)} {Number(g.device_count || 0) === 1 ? 'device' : 'devices'}
                      {g.schedule_name ? ` · ${g.schedule_name}` : ''}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="ml-5 mt-2 mb-3 grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                      {/* Members */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-500 mb-2">Members ({members.length})</h3>
                        {members.length === 0
                          ? <p className="text-xs text-gray-400 italic">None</p>
                          : <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                              {members.map((m) => (
                                <li key={m.id} className="py-1.5 flex items-center justify-between">
                                  <span className="text-gray-900">{`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}</span>
                                  <span className="text-[11px] text-gray-500 truncate ml-2">{m.email || m.phone || ''}</span>
                                  {m.intrlock_person_id && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">linked</span>}
                                </li>
                              ))}
                            </ul>}
                      </div>

                      {/* Door access */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-500 mb-2">Door access ({devices.length})</h3>
                        {devices.length === 0
                          ? <p className="text-xs text-gray-400 italic">None — {L.providerName} hasn't surfaced device-level access for this group.</p>
                          : <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                              {devices.map((d) => (
                                <li key={d.id} className="py-1.5">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-gray-900">{d.name || 'Unnamed door'}</span>
                                    <span className="text-[11px] text-gray-500">{intrlockTypeLabel(d)}</span>
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    {d.cloud_node_name || ''}
                                    {d.property_name ? ` · ${d.property_name}` : ''}
                                    {d.schedule_name ? ` · ${d.schedule_name}` : ''}
                                  </div>
                                </li>
                              ))}
                            </ul>}
                      </div>

                      {/* Schedules */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-500 mb-2">Access schedules ({schedules.length})</h3>
                        {schedules.length === 0
                          ? <p className="text-xs text-gray-400 italic">None</p>
                          : <ul className="space-y-2 max-h-72 overflow-y-auto">
                              {schedules.map((s) => (
                                <li key={s.id} className="text-xs">
                                  <p className="font-medium text-gray-900">{s.name || 'Unnamed schedule'}</p>
                                  {s.weekly_json && (
                                    <pre className="mt-1 text-[10px] text-gray-500 bg-gray-50 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap">
                                      {typeof s.weekly_json === 'string' ? s.weekly_json : JSON.stringify(s.weekly_json, null, 2)}
                                    </pre>
                                  )}
                                </li>
                              ))}
                            </ul>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
    </Card>
  );
};

const Chip: React.FC<{ label: string; value: any; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={highlight ? 'attention' : ''}>
    <span>{label}</span>
    <strong>{value ?? 0}</strong>
  </div>
);
